/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosImageFragment, LogoFragment } from "@cosmos/hygraph-schema";
import { useCosmosComponentContext } from "@components/context";
import { getHygraphImageIds, isHygraphImageSrc } from "@components/utils";

const isSvg = (img: CosmosImageFragment): boolean => {
	const mimeType: string[] = img.mimeType ? img.mimeType.split("/") : [];
	return mimeType.length === 2 && "svg+xml" === mimeType[1];
};

export const useHygraphLogoUrl = (img?: CosmosImageFragment): string => {
	const { hygraphMediaDomain } = useCosmosComponentContext();
	if (!img || !img.url) return "";

	//Only try for hygraph assets
	if (!isHygraphImageSrc(hygraphMediaDomain, img.url)) return img.url;

	if (isSvg(img)) return img.url;

	const { assetId, environmentId } = getHygraphImageIds(hygraphMediaDomain, img.url);
	if (!assetId || !img.height) return img.url;

	const envPartStr = environmentId ? `/${environmentId}` : "";

	return `https://${hygraphMediaDomain}/${assetId}/quality=value:90/auto_image${envPartStr}`;
};

// Mirrors the LogoList / Logo layout: `eds.dimension.64` between logos,
// `eds.dimension.32` inline padding, landscape logos at 2/3 of the row height.
const LOGO_GAP = 64;
const LIST_PADDING_INLINE = 32;
const LANDSCAPE_SCALE = 2 / 3;

/**
 * Width of a single-row LogoList, derived from the authored asset dimensions
 * rather than measured from the DOM.
 *
 * Returns `null` when any logo has no dimensions: the width can't be known, and
 * guessing low would pick grid mode for a row that wraps out of its fixed height.
 */
export const getLogoListWidth = (logos: LogoFragment[], height: number): number | null => {
	if (!logos.length) return 0;
	if (logos.some(({ image }) => !image?.width || !image?.height)) return null;

	const logosWidth = logos.reduce((total, { image, landscape }) => {
		const logoHeight = landscape ? height * LANDSCAPE_SCALE : height;
		return total + logoHeight * (image.width! / image.height!);
	}, 0);

	return Math.ceil(logosWidth + LOGO_GAP * (logos.length - 1) + LIST_PADDING_INLINE * 2);
};
